import type { AudioEngineSnapshot, SessionPlayback } from '../audio/audioEngine';
import { AudioBlockedError, SessionStartCancelled } from '../audio/audioEngine';
import { timingOf } from '../animation/clock';
import { sessionDuration } from '../domain/avOps';
import type { AvSession, Project } from '../domain/schemas';
import { Observable } from '../lib/store';
import { extractEvents, type ExtractedEvents } from './events';
import { addCueOverlays } from './cues';
import { emptyReaction, evaluateReactions, type ReactionFrame } from './reactions';
import { SessionClock, type SessionTiming } from './sessionClock';
import { secondsToTicks, ticksToSeconds } from './time';
import type { AudioFeatures } from '../visualizer/analysis';
import { sessionVisualizer, type VisualizerFrame } from '../visualizer/evaluate';

/** Clock timing of a session: its length, loop range (seconds) and where the animation starts. */
export function sessionTiming(project: Project, s: AvSession): SessionTiming {
  const bpm = project.settings.bpm;
  const durationSec = sessionDuration(project, s);
  const loopTicks = sessionLoopTicks(project, s);
  const anim = s.animationId ? project.animations.find((a) => a.id === s.animationId) : undefined;
  return {
    durationSec,
    loop: loopTicks ? { start: ticksToSeconds(loopTicks.startTicks, bpm), end: ticksToSeconds(loopTicks.endTicks, bpm) } : null,
    animationStartSec: ticksToSeconds(s.animationStartTick, bpm),
    animation: timingOf(anim),
  };
}

/** The tick range the transport loops (and the clock folds): the loop, or the whole session when its end loops. */
export function sessionLoopTicks(project: Project, s: AvSession): SessionPlayback['loop'] {
  if (s.loop.enabled) return { startTicks: s.loop.startTick, endTicks: s.loop.endTick };
  if (s.endBehavior === 'loop') return { startTicks: 0, endTicks: Math.max(1, Math.round(secondsToTicks(sessionDuration(project, s), project.settings.bpm))) };
  return null;
}

/**
 * Reactions and motion-cue overlays of a session at a session time. Pure: the live preview
 * and video export (Phase 4B) both evaluate frames through this one function.
 */
export function sessionReactions(project: Project, s: AvSession, x: ExtractedEvents, seconds: number): ReactionFrame | null {
  if (s.mappings.length === 0 && s.motionCues.length === 0) return null;
  const frame: ReactionFrame = s.mappings.length ? evaluateReactions(s, x, seconds) : { objects: new Map(), effects: { flash: null, background: null, shake: { x: 0, y: 0 } }, active: 0 };
  const scene = s.motionCues.length ? project.scenes.find((sc) => sc.id === s.sceneId) : undefined;
  if (scene) frame.active += addCueOverlays(frame.objects, s, scene, project.characters, project.settings.bpm, seconds, emptyReaction);
  return frame;
}

/** The part of the audio engine a session drives (an interface so tests can fake it). */
export interface SessionAudio {
  readonly state: { get(): Pick<AudioEngineSnapshot, 'status'>; subscribe(listener: () => void): () => void };
  startSession(playback: SessionPlayback): Promise<number>;
  stop(): Promise<void>;
  audibleTime(): number | null;
  /** Diagnostics: the transport's own tick position at a context time. */
  transportTicksAt?(contextTime: number): number | null;
}

export type SyncStatus = 'stopped' | 'starting' | 'playing' | 'paused' | 'ended';

export interface SyncSnapshot {
  status: SyncStatus;
  /** Which clock drives the session: the audio context, or the silent system fallback. */
  source: 'audio' | 'system' | null;
  /** Why playback is silent or audio failed (muted, blocked, error), or null. */
  notice: string | null;
  sessionId: string | null;
}

/**
 * Owns playback of one audio-visual session (Phase 4A): one SessionClock (the authoritative
 * position), the shared AudioEngine while audio plays, extracted events and reaction
 * evaluation. The Sync preview renderer follows `clock`; the timeline reads `clock` too.
 *
 * Ownership: while a session plays it owns the audio engine (`startSession`); the
 * Animation workspace's PlaybackClock is paused via `onTakeover`. If something else stops
 * the engine (the Music workspace's Stop), the session pauses where it is.
 *
 * Audio failure policy: muted sessions and sessions without an engine run on the system
 * clock and say so; when the browser blocks audio (autoplay policy) or audio fails, the
 * session plays silently on the system clock with a notice — never pretending to be in sync
 * with sound that is not playing. Rapid play/pause/seek requests are serialised by a token:
 * only the newest request may start the clock.
 */
export class AVSessionController {
  readonly clock = new SessionClock();
  readonly state = new Observable<SyncSnapshot>({ status: 'stopped', source: null, notice: null, sessionId: null });
  /** Called when a session starts playing (pause other playback). */
  private onTakeover: (() => void) | null = null;

  private project: Project | null = null;
  private session: AvSession | null = null;
  private token = 0;
  private extractedKey: unknown[] = [];
  private extracted: ExtractedEvents | null = null;
  private endTimer: ReturnType<typeof setTimeout> | null = null;
  private ownsAudio = false;
  /** What the running (or starting) playback was started with: mute, music, loop ticks, tempo. */
  private playbackKey: string | null = null;
  private readonly unsubscribeEngine: (() => void) | null;

  constructor(
    private readonly audio: SessionAudio | null,
    /** Phase 4C: measured audio features for visualizers (cached analysis; never computed here). */
    private readonly analysis: { peek(project: Project, session: AvSession): AudioFeatures | null } | null = null,
  ) {
    this.unsubscribeEngine = audio?.state.subscribe(this.onEngine) ?? null;
    this.clock.onFinish = () => {
      if (this.state.get().status !== 'playing') return;
      this.token++;
      this.clearEndTimer();
      void this.releaseAudio();
      this.state.update({ status: 'ended' });
    };
  }

  /** Registers what to pause when a session takes over playback (the Animation workspace clock). */
  setTakeover(callback: (() => void) | null): void {
    this.onTakeover = callback;
  }

  get current(): AvSession | null {
    return this.session;
  }

  /** Updates the project and the selected session (null: none). Switching sessions stops and rewinds. */
  load(project: Project | null, sessionId: string | null): void {
    const previous = this.session;
    this.project = project;
    const session = (project && sessionId && project.avSessions.find((s) => s.id === sessionId)) || null;
    this.session = session;
    if (!session || session.id !== previous?.id) {
      if (previous) this.stopNow();
      this.clock.seek(0);
      this.state.update({ sessionId: session?.id ?? null, notice: null });
    }
    if (!project || !session) return;
    this.clock.setTiming(this.timing(project, session));
    const s = this.state.get();
    // A change of what the engine plays — mute, music source, loop range or tempo — restarts
    // it at the current position, also while a start is still in flight (that start would
    // otherwise complete with the old source). Compared by value: session edits re-create
    // these objects, and reaction, marker or cue edits must never restart the audio.
    const active = s.status === 'playing' || s.status === 'starting';
    if (active && previous && this.keyFor(project, session) !== this.playbackKey) void this.play();
    else if (s.status === 'playing') this.armEndTimer();
  }

  private timing(project: Project, s: AvSession): SessionTiming {
    return sessionTiming(project, s);
  }

  private keyFor(project: Project, s: AvSession): string {
    return JSON.stringify([s.muted, s.music, this.loopTicks(project, s), project.settings.bpm]);
  }

  private loopTicks(project: Project, s: AvSession): SessionPlayback['loop'] {
    return sessionLoopTicks(project, s);
  }

  /** Extracted events of the current session (memoised on everything extraction reads). */
  events(): ExtractedEvents | null {
    const p = this.project;
    const s = this.session;
    if (!p || !s) return null;
    const key = [p.patterns, p.arrangement, p.audioClips, p.settings.bpm, p.settings.swing, s.music, s.markers];
    if (!this.extracted || key.some((k, i) => k !== this.extractedKey[i])) {
      this.extracted = extractEvents(p, s);
      this.extractedKey = key;
    }
    return this.extracted;
  }

  /** Reactions and motion-cue overlays at a session time (pure; the renderer calls this every displayed frame). */
  reactions(seconds: number): ReactionFrame | null {
    const p = this.project;
    const s = this.session;
    const x = this.events();
    if (!p || !s || !x) return null;
    return sessionReactions(p, s, x, seconds);
  }

  /** Visualizer layers at a session time (pure; the preview renderer calls this every displayed frame). */
  visualizer(seconds: number): VisualizerFrame | null {
    const p = this.project;
    const s = this.session;
    if (!p || !s || s.visualizer.layers.length === 0) return null;
    const x = this.events();
    if (!x) return null;
    return sessionVisualizer(p, s, x, this.analysis?.peek(p, s) ?? null, seconds);
  }

  /** Plays from the current position (from the start when the session has ended). */
  async play(): Promise<void> {
    const p = this.project;
    const s = this.session;
    if (!p || !s) return;
    const token = ++this.token;
    this.playbackKey = this.keyFor(p, s);
    this.clearEndTimer();
    let from = this.clock.seconds();
    const timing = this.clock.get().timing;
    if (!timing.loop && from >= timing.durationSec - 1e-6) from = 0;
    // The transport starts on a whole tick; the clock starts on exactly that tick too.
    const fromTicks = Math.round(secondsToTicks(from, p.settings.bpm));
    from = ticksToSeconds(fromTicks, p.settings.bpm);
    this.clock.pause();
    this.clock.seek(from);
    this.onTakeover?.();
    if (s.muted || !this.audio) {
      if (this.ownsAudio) await this.releaseAudio();
      if (token !== this.token) return;
      this.clock.start(from, { kind: 'system' });
      this.state.update({ status: 'playing', source: 'system', notice: s.muted ? 'Muted: the preview runs on the system clock.' : 'Audio is unavailable: the preview runs silently.' });
      this.armEndTimer();
      return;
    }
    this.state.update({ status: 'starting', notice: null });
    this.ownsAudio = true;
    try {
      const at = await this.audio.startSession({ music: s.music, fromTicks, loop: this.loopTicks(p, s) });
      if (token !== this.token) return;
      this.clock.start(from, { kind: 'audio', at, audioNow: () => this.audio!.audibleTime() });
      this.state.update({ status: 'playing', source: 'audio', notice: null });
      this.armEndTimer();
    } catch (error) {
      if (error instanceof SessionStartCancelled || token !== this.token) return;
      this.ownsAudio = false;
      this.clock.start(from, { kind: 'system' });
      const why = error instanceof AudioBlockedError ? error.message : `Audio could not start (${error instanceof Error ? error.message : 'unknown error'}); the preview runs silently.`;
      this.state.update({ status: 'playing', source: 'system', notice: why });
      this.armEndTimer();
    }
  }

  /**
   * Drift diagnostics: the clock position and the audio transport's own position (from
   * Tone's tick clock, folded the same way) at the same audible instant.
   */
  measureDrift(): { clock: number; transport: number | null; source: 'audio' | 'system' | null } {
    const source = this.state.get().source;
    const p = this.project;
    const at = this.audio?.audibleTime();
    if (source !== 'audio' || !p || at == null || !this.audio?.transportTicksAt) return { clock: this.clock.seconds(), transport: null, source };
    const ticks = this.audio.transportTicksAt(at);
    return { clock: this.clock.secondsAt(at), transport: ticks === null ? null : ticksToSeconds(ticks, p.settings.bpm), source };
  }

  pause(): void {
    this.token++;
    this.clearEndTimer();
    this.clock.pause();
    void this.releaseAudio();
    if (this.state.get().status === 'playing' || this.state.get().status === 'starting') this.state.update({ status: 'paused', source: null });
  }

  toggle(): Promise<void> | void {
    const status = this.state.get().status;
    return status === 'playing' || status === 'starting' ? this.pause() : this.play();
  }

  /** Moves to a session time; keeps playing (the transport restarts there) if it was. */
  seek(seconds: number): Promise<void> | void {
    const status = this.state.get().status;
    const wasPlaying = status === 'playing' || status === 'starting';
    if (wasPlaying) {
      this.token++;
      this.clock.pause();
    }
    this.clock.seek(seconds);
    if (status === 'ended') this.state.update({ status: 'paused' });
    if (wasPlaying) return this.play();
  }

  /** Back to the start and play. */
  restart(): Promise<void> {
    this.token++;
    this.clock.pause();
    this.clock.seek(0);
    return this.play();
  }

  /** Stop: pause and rewind. */
  stop(): void {
    this.stopNow();
    this.clock.seek(0);
  }

  private stopNow(): void {
    this.token++;
    this.clearEndTimer();
    this.clock.pause();
    void this.releaseAudio();
    this.state.update({ status: 'stopped', source: null });
  }

  /** Closing the project: stop everything and forget it. */
  unload(): void {
    this.stopNow();
    this.project = null;
    this.session = null;
    this.extracted = null;
    this.extractedKey = [];
    this.clock.seek(0);
    this.state.set({ status: 'stopped', source: null, notice: null, sessionId: null });
  }

  dispose(): void {
    this.unload();
    this.unsubscribeEngine?.();
    this.clock.onFinish = null;
  }

  private async releaseAudio(): Promise<void> {
    if (!this.ownsAudio || !this.audio) return;
    this.ownsAudio = false;
    await this.audio.stop();
  }

  /** Something else stopped the engine (Music workspace Stop, an audio error): pause here. */
  private readonly onEngine = (): void => {
    const status = this.audio!.state.get().status;
    if (!this.ownsAudio || this.state.get().status !== 'playing' || this.state.get().source !== 'audio') return;
    if (status === 'stopped' || status === 'error' || status === 'idle') {
      this.ownsAudio = false;
      this.token++;
      this.clearEndTimer();
      this.clock.pause();
      this.state.update({ status: 'paused', source: null, notice: status === 'error' ? 'Audio stopped with an error; playback paused.' : null });
    }
  };

  /** Ends a non-looping session on time even when no renderer is ticking. */
  private armEndTimer(): void {
    this.clearEndTimer();
    const t = this.clock.get().timing;
    if (t.loop || !this.clock.get().playing) return;
    const remaining = t.durationSec - this.clock.seconds();
    this.endTimer = setTimeout(() => {
      this.endTimer = null;
      if (!this.clock.get().playing) return;
      if (this.clock.sample().ended) this.clock.finish();
      else this.armEndTimer();
    }, Math.max(0, remaining * 1000 + 30));
  }

  private clearEndTimer(): void {
    if (this.endTimer !== null) clearTimeout(this.endTimer);
    this.endTimer = null;
  }
}
